import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowRight, Save, Play, Plus, Trash2, Image as ImageIcon, 
  Type, Settings, Check, X as XIcon, RotateCw, Layers, Film, Copy,
  ChevronDown, Grid, Eye, Timer, CheckCircle
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
// تم استئصال الاستيراد القاتل: import AWS from 'aws-sdk';

// ==========================================
// المحركات التفاعلية المجردة (مطابقة للعرض)
// ==========================================

const MatchingGame = ({ pairs, onComplete }) => {
  const [gameCards, setGameCards] = useState([]);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);

  const initializeGame = () => {
    setIsShuffling(true);
    let c = [];
    pairs.forEach(p => {
      if (p.item1 && p.item1.content) c.push({ id: `${p.id}a`, pairId: p.id, ...p.item1 });
      if (p.item2 && p.item2.content) c.push({ id: `${p.id}b`, pairId: p.id, ...p.item2 });
      else if (p.item1 && p.item1.content) c.push({ id: `${p.id}b`, pairId: p.id, ...p.item1 }); 
    });
    setGameCards(c.sort(() => Math.random() - 0.5));
    setMatchedPairs([]); setFlippedIndices([]);
    setTimeout(() => setIsShuffling(false), 800);
  };

  useEffect(() => { if (pairs && pairs.length > 0) initializeGame(); }, [pairs]);

  const isWon = pairs && pairs.length > 0 && matchedPairs.length === pairs.length;
  let gridClass = gameCards.length > 12 ? (gameCards.length >= 24 ? 'grid-cols-5 md:grid-cols-8 lg:grid-cols-10' : 'grid-cols-4 md:grid-cols-6 lg:grid-cols-8') : 'grid-cols-3 md:grid-cols-4';

  if (!pairs || pairs.length === 0) return null;

  return (
    <div className="w-full h-full flex flex-col relative bg-[#1a202c] overflow-hidden p-4 md:p-8 items-center justify-center rounded-[2.5rem] shadow-2xl border-4 border-slate-800">
      {isShuffling && (<div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/90 backdrop-blur-md"><RotateCw className="w-12 h-12 text-amber-500 animate-spin mb-4" /><h3 className="text-amber-500 font-bold text-2xl animate-pulse italic drop-shadow-lg">جاري التوزيع...</h3></div>)}
      {isWon && !isShuffling && (
        <div className="absolute inset-0 z-[60] flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-md">
          <div className="bg-white/10 p-12 rounded-[3rem] border border-white/20 text-center shadow-2xl"><CheckCircle className="w-24 h-24 text-emerald-400 mb-6 mx-auto drop-shadow-lg" /><h2 className="text-4xl font-black text-white mb-8">أداء مذهل!</h2>
            <div className="flex gap-4 justify-center"><button onClick={initializeGame} className="px-8 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-full font-black"><RotateCw className="inline-block mr-2" size={20}/> إعادة</button><button onClick={onComplete} className="px-8 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full font-black"><Check className="inline-block mr-2" size={20}/> إنهاء المعاينة</button></div>
          </div>
        </div>
      )}
      <div className={`grid gap-2 md:gap-4 w-full max-w-7xl h-auto max-h-[80vh] ${gridClass} content-center justify-center mx-auto`}>
        {!isShuffling && gameCards.map((card, idx) => {
          const isF = flippedIndices.includes(idx) || matchedPairs.includes(card.pairId);
          const isM = matchedPairs.includes(card.pairId);
          return (
            <div key={idx} onClick={() => {
              if (flippedIndices.length === 2 || isF || isM) return;
              const nf = [...flippedIndices, idx]; setFlippedIndices(nf);
              if (nf.length === 2) {
                if (gameCards[nf[0]].pairId === gameCards[nf[1]].pairId) setTimeout(() => { setMatchedPairs([...matchedPairs, card.pairId]); setFlippedIndices([]); }, 700);
                else setTimeout(() => setFlippedIndices([]), 1000);
              }
            }} className={`relative aspect-[4/3] w-full transition-all duration-500 cursor-pointer ${isM ? 'opacity-0 scale-90 pointer-events-none' : 'hover:scale-105'}`} style={{transformStyle: 'preserve-3d'}}>
               <div className="w-full h-full absolute inset-0 transition-transform duration-700 shadow-xl rounded-xl" style={{transformStyle: 'preserve-3d', transform: isF ? 'rotateY(180deg)' : 'rotateY(0deg)'}}>
                  <div className="absolute inset-0 bg-amber-600 rounded-xl border-2 md:border-4 border-amber-900 flex items-center justify-center overflow-hidden shadow-inner" style={{backfaceVisibility: 'hidden', backgroundImage: 'url("https://www.transparenttextures.com/patterns/wood-pattern.png")'}}><span className="text-2xl font-black text-amber-900/40">{idx + 1}</span></div>
                  <div className="absolute inset-0 bg-white rounded-xl border-2 md:border-4 border-white flex items-center justify-center overflow-hidden shadow-inner" style={{backfaceVisibility: 'hidden', transform: 'rotateY(180deg)'}}>
                     {card.type === 'text' ? <span className="p-1 text-xs font-bold text-center text-slate-800 leading-tight">{card.content}</span> : <img src={card.content} className="w-full h-full object-contain p-2" />}
                  </div>
               </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SequencingGame = ({ steps, onComplete }) => {
  const [slots, setSlots] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isWon, setIsWon] = useState(false);

  const initializeGame = () => { setIsWon(false);
     setSlots(new Array(steps.length).fill(null)); 
     setAvailableItems([...steps].sort(() => Math.random() - 0.5)); };
  useEffect(() => { if (steps && steps.length > 0) initializeGame(); }, [steps]);

  const handleDrop = (index) => {
    if (!draggedItem) return;
    const newSlots = [...slots];
    if (newSlots[index]) setAvailableItems(p => [...p, newSlots[index]]);
    newSlots[index] = draggedItem; setSlots(newSlots);
    setAvailableItems(p => p.filter(item => item.id !== draggedItem.id)); setDraggedItem(null);
    if (newSlots.every(s => s !== null)) {
      if (newSlots.every((s, i) => s.id === steps[i].id)) setIsWon(true);
      else setTimeout(() => initializeGame(), 1500);
    }
  };

  if (!steps || steps.length === 0) return null;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-slate-950 rounded-[2.5rem] relative overflow-hidden shadow-2xl border-4 border-slate-800">
      {isWon && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-md"><div className="bg-white/10 p-12 rounded-[3rem] text-center shadow-2xl"><CheckCircle className="w-24 h-24 text-emerald-400 mb-6 mx-auto" /><h2 className="text-4xl font-black text-white mb-8">ترتيب مثالي!</h2><div className="flex gap-4 justify-center"><button onClick={initializeGame} className="px-8 py-3 bg-amber-600 text-white rounded-full font-black">إعادة</button><button onClick={onComplete} className="px-8 py-3 bg-emerald-600 text-white rounded-full font-black">إنهاء</button></div></div></div>
      )}
      <div className="w-full max-w-5xl bg-[#111827] p-8 rounded-[2rem] border-y-8 border-black shadow-2xl flex gap-4 items-center justify-center min-h-[180px] mb-12">
        {slots.map((s, i) => (
          <div key={i} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(i)} className={`w-32 aspect-[4/3] rounded-xl border-4 transition-all flex items-center justify-center overflow-hidden ${s ? 'border-amber-500 bg-white' : 'border-dashed border-slate-700 bg-slate-900/50'}`}>
            {!s ? <span className="text-slate-700 text-4xl font-black">{i + 1}</span> : (s.type === 'image' ? <img src={s.content} className="object-contain w-full h-full p-2" /> : <span className="p-2 text-xs font-black text-slate-900 text-center">{s.content}</span>)}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 justify-center max-w-5xl">
        {availableItems.map((c, i) => (
          <div key={i} draggable onDragStart={() => setDraggedItem(c)} className="w-32 aspect-[4/3] bg-white rounded-xl border-4 border-amber-600 cursor-grab active:cursor-grabbing hover:scale-105 flex items-center justify-center overflow-hidden">
            {c.type === 'image' ? <img src={c.content} className="object-contain p-2 w-full h-full pointer-events-none" /> : <div className="p-2 text-xs font-black text-center pointer-events-none">{c.content}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const SortingGame = ({ categories, onComplete }) => {
  const [progress, setProgress] = useState({});
  const [availableItems, setAvailableItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isWon, setIsWon] = useState(false);

  const initializeGame = () => {
    setIsWon(false); setProgress({}); let allItems = [];
    categories.forEach(cat => cat.items.forEach(item => allItems.push({ ...item, correctCategory: cat.id })));
    setAvailableItems(allItems.sort(() => Math.random() - 0.5));
  };
  useEffect(() => { if (categories && categories.length > 0) initializeGame(); }, [categories]);

  const handleDrop = (categoryId) => {
    if (!draggedItem) return;
    if (draggedItem.correctCategory === categoryId) {
      setProgress(p => ({ ...p, [categoryId]: [...(p[categoryId] || []), draggedItem] }));
      setAvailableItems(p => p.filter(item => item.id !== draggedItem.id));
      if (availableItems.length === 1) setIsWon(true);
    }
    setDraggedItem(null);
  };

  if (!categories || categories.length === 0) return null;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-[#0f172a] rounded-[2.5rem] relative overflow-hidden shadow-2xl border-4 border-slate-800">
      {isWon && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-md"><div className="bg-white/10 p-12 rounded-[3rem] text-center shadow-2xl"><CheckCircle className="w-24 h-24 text-emerald-400 mb-6 mx-auto" /><h2 className="text-4xl font-black text-white mb-8">تصنيف دقيق!</h2><div className="flex gap-4 justify-center"><button onClick={initializeGame} className="px-8 py-3 bg-amber-600 text-white rounded-full font-black">إعادة</button><button onClick={onComplete} className="px-8 py-3 bg-emerald-600 text-white rounded-full font-black">إنهاء</button></div></div></div>
      )}
      <div className="mb-12 h-44 flex items-center justify-center">
        {availableItems.length > 0 && (
          <div draggable onDragStart={() => setDraggedItem(availableItems[0])} className="w-56 aspect-[4/3] bg-white rounded-2xl border-4 border-amber-500 shadow-2xl cursor-grab active:cursor-grabbing flex items-center justify-center overflow-hidden hover:scale-110 transition-transform">
            {availableItems[0].type === 'image' ? <img src={availableItems[0].content} className="w-full h-full object-contain p-2 pointer-events-none" /> : <span className="p-4 text-xl font-black text-slate-900 text-center pointer-events-none">{availableItems[0].content}</span>}
          </div>
        )}
      </div>
      <div className="w-full max-w-6xl bg-slate-900/80 rounded-[3rem] border-4 border-slate-700 shadow-2xl overflow-hidden min-h-[300px] flex divide-x-2 divide-slate-700 flex-row-reverse">
        {categories.map(cat => (
          <div key={cat.id} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(cat.id)} className="flex-1 flex flex-col p-6 items-center hover:bg-slate-800/50 transition-colors">
             <div className="mb-6 py-2 px-6 bg-slate-800 rounded-full border border-slate-600"><span className="text-sm font-black text-indigo-400">{cat.title}</span></div>
             <div className="flex flex-wrap gap-2 justify-center">
               {(progress[cat.id] || []).map((item, idx) => (
                 <div key={idx} className="w-20 aspect-[4/3] bg-white rounded-xl flex items-center justify-center shadow-md border-2 border-indigo-100 overflow-hidden">
                   {item.type === 'image' ? <img src={item.content} className="object-contain p-1 w-full h-full" /> : <span className="text-[10px] font-black p-1 text-center text-slate-800">{item.content}</span>}
                 </div>
               ))}
             </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MysteryGame = ({ data, onComplete }) => {
  const [revealedTiles, setRevealedTiles] = useState([]);
  const [choices, setChoices] = useState([]);
  const [isWon, setIsWon] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const initializeGame = () => {
    setIsWon(false); setRevealedTiles([]); setFeedback(null);
    if (data && data.correctAnswer) setChoices([data.correctAnswer, ...(data.distractors || [])].filter(c => c.trim() !== '').sort(() => Math.random() - 0.5));
  };
  useEffect(() => { initializeGame(); }, [data]);

  const handleChoice = (choice) => {
    if (choice === data.correctAnswer) { setIsWon(true); setRevealedTiles(Array.from({length: data.tileCount || 16}, (_, i) => i)); }
    else { setFeedback('error'); setTimeout(() => setFeedback(null), 1000); }
  };

  if (!data || !data.image) return null;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-slate-950 rounded-[2.5rem] relative overflow-hidden shadow-2xl border-4 border-slate-800">
      {isWon && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/80 backdrop-blur-sm"><div className="bg-white/10 p-12 rounded-[3rem] text-center shadow-2xl"><CheckCircle className="w-24 h-24 text-emerald-400 mb-6 mx-auto" /><h2 className="text-4xl font-black text-white mb-8">إجابة صحيحة!</h2><div className="flex gap-4 justify-center"><button onClick={initializeGame} className="px-8 py-3 bg-amber-600 text-white rounded-full font-black">إعادة</button><button onClick={onComplete} className="px-8 py-3 bg-emerald-600 text-white rounded-full font-black">إنهاء</button></div></div></div>
      )}
      <div className={`relative w-full max-w-3xl aspect-video bg-black rounded-[2rem] border-[10px] border-slate-800 shadow-2xl overflow-hidden transition-all ${feedback === 'error' ? 'border-red-500' : ''}`}>
        <img src={data.image} className="w-full h-full object-contain p-4" />
        <div className="absolute inset-0 grid gap-1 p-1" style={{ gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt((data.tileCount || 16) * 1.6))}, 1fr)` }}>
          {[...Array(data.tileCount || 16)].map((_, i) => (
            <div key={i} onClick={() => !revealedTiles.includes(i) && setRevealedTiles(p => [...p, i])} className={`bg-slate-700/90 backdrop-blur-xl border border-slate-600 rounded-lg cursor-pointer transition-all duration-700 flex items-center justify-center ${revealedTiles.includes(i) ? 'opacity-0 scale-150 pointer-events-none' : 'hover:bg-slate-600'}`}><span className="text-white/20 font-black">{i + 1}</span></div>
          ))}
        </div>
      </div>
      <div className="mt-12 flex flex-wrap gap-6 justify-center">
        {choices.map((c, i) => <button key={i} onClick={() => handleChoice(c)} className="px-10 py-4 bg-slate-800 hover:bg-blue-600 text-white rounded-full font-black text-xl shadow-xl">{c}</button>)}
      </div>
    </div>
  );
};

const ActivityStudio = () => {
  const navigate = useNavigate();
  const location = useLocation(); 
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState('matching');
  const [mode, setMode] = useState('edit'); 
  const [activityTitle, setActivityTitle] = useState(
    location.state?.draft?.lessonTitle || 
    JSON.parse(localStorage.getItem('tamken_lesson_draft') || '{}').lessonTitle || 
    "نشاط تعليمي جديد"
  );
  useEffect(() => {
    const draftRaw = localStorage.getItem('tamken_lesson_draft');
    if (draftRaw) {
      const draft = JSON.parse(draftRaw);
      if (draft.lessonTitle && draft.lessonTitle !== activityTitle) {
        setActivityTitle(draft.lessonTitle);
      }
    }
  }, []); 
  const [isIdenticalMode, setIsIdenticalMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const fileInputRef = useRef(null);
  const activeUploadRef = useRef(null);
  const scrollEndRef = useRef(null);
  const menuRef = useRef(null);
  const timerRef = useRef(null);

  const [matchingPairs, setMatchingPairs] = useState([{ id: '1', item1: { type: 'text', content: '' }, item2: { type: 'text', content: '' } }]);
  const [sequenceItems, setSequenceItems] = useState([{ id: 's1', type: 'text', content: '' }]);
  const [sortGroups, setSortGroups] = useState([
    { id: 'g1', title: 'المجموعة 1', items: [{ id: 'gi1', type: 'text', content: '' }] },
    { id: 'g2', title: 'المجموعة 2', items: [{ id: 'gi2', type: 'text', content: '' }] }
  ]);
  const [mysteryData, setMysteryData] = useState({ image: '', correctAnswer: '', distractors: ['إجابة خاطئة 1'], tileCount: 16 });

  const [gameCards, setGameCards] = useState([]);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [showFeedback, setShowFeedback] = useState(null);
  const [isShuffling, setIsShuffling] = useState(false);
  const [slots, setSlots] = useState([]); 
  const [draggedCard, setDraggedCard] = useState(null);
  const [sortProgress, setSortProgress] = useState({});
  const [revealedTiles, setRevealedTiles] = useState([]);
  const [gameTime, setGameTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [shuffledChoices, setShuffledChoices] = useState([]);

  useEffect(() => {
    if (isTimerRunning) timerRef.current = setInterval(() => setGameTime(p => p + 1), 1000);
    else clearInterval(timerRef.current);
    return () => clearInterval(timerRef.current);
  }, [isTimerRunning]);

  useEffect(() => {
    if (mode === 'edit' && scrollEndRef.current) scrollEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [matchingPairs.length, sequenceItems.length, sortGroups.length, mode]);

  // ==============================================================
  // التدخل الجراحي: الرفع الآمن وتجنب الاعتمادية الميتة (aws-sdk)
  // ==============================================================
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // 1. طلب تصريح الرفع الآمن من الخادم الوسيط
      const response = await fetch('/.netlify/functions/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileName: file.name, fileType: file.type })
      });

      if (!response.ok) throw new Error('فشل الحصول على تصريح الرفع');
      const { uploadUrl, fileUrl } = await response.json();

      // 2. الرفع المباشر إلى التخزين السحابي
         const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 
          'Content-Type': file.type,
         'x-amz-acl': 'public-read' // <--- التدخل الجراحي يجب أن يكون داخل أقواس الـ headers
          },
         body: file
         });

      if (!uploadResponse.ok) throw new Error('فشل الرفع إلى التخزين السحابي');

      const imageUrl = fileUrl;
      
      if (activeTemplate === 'mystery') {
        setMysteryData(prev => ({ ...prev, image: imageUrl }));
        return;
      }

      if (!activeUploadRef.current) return;
      
      // استقبال subId الذي تم إصلاحه لمعالجة لعبة التصنيف بشكل صحيح
      const { id, key, subId } = activeUploadRef.current;

      if (activeTemplate === 'matching') {
        setMatchingPairs(prev => prev.map(p => p.id === id ? { ...p, [key]: { type: 'image', content: imageUrl } } : p));
      } else if (activeTemplate === 'sequencing') {
        setSequenceItems(prev => prev.map(it => it.id === id ? { ...it, type: 'image', content: imageUrl } : it));
      } else if (activeTemplate === 'sorting') {
        setSortGroups(prev => prev.map(g => g.id === id ? { ...g, items: g.items.map(it => it.id === subId ? { ...it, type: 'image', content: imageUrl } : it) } : g));
      }

    } catch (error) {
      console.error("Upload error:", error);
      alert("حدث خطأ أثناء الرفع. المرجو التحقق من الاتصال.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // تصفير الحقل ليقبل صوراً جديدة
      }
    }
  };

  // تم إصلاح الدالة لتستقبل subId لمعالجة عناصر التصنيف المتداخلة
  const triggerUpload = (id, key, subId) => {
    activeUploadRef.current = { id, key, subId };
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; 
      fileInputRef.current.click();
    }
  };

  const onDragStart = (e, card) => setDraggedCard(card);
  const onDropSequencing = (e, i) => {
    if (!draggedCard) return;
    const ns = [...slots]; if (ns[i]) setGameCards(p => [...p, ns[i]]);
    ns[i] = draggedCard; setSlots(ns); setGameCards(p => p.filter(c => c.id !== draggedCard.id)); setDraggedCard(null);
    if (ns.every(s => s !== null)) {
      const ok = ns.every((s, idx) => s.id === sequenceItems[idx].id);
      setShowFeedback(ok ? 'success' : 'error');
      if (!ok) setTimeout(() => initializeGame(), 2000);
    }
  };
  const onDropSorting = (e, gid) => {
    if (!draggedCard || draggedCard.groupId !== gid) { setShowFeedback('error'); setTimeout(() => setShowFeedback(null), 1000); return; }
    const np = { ...sortProgress, [gid]: [...(sortProgress[gid] || []), draggedCard] };
    setSortProgress(np); setGameCards(p => p.filter(c => c.id !== draggedCard.id));
    const total = sortGroups.reduce((s, g) => s + g.items.length, 0);
    if (Object.values(np).flat().length === total) setShowFeedback('success');
    setDraggedCard(null);
  };

  const initializeGame = () => {
    setIsMenuOpen(false);
    setMode('play');
  };
  
  const handleFinalSave = async () => {
    setIsSaving(true);
    console.log("بدء عملية التجميع والحفظ...");
    
    try {
      const draft = location.state?.draft || JSON.parse(localStorage.getItem('tamken_lesson_draft') || '{}');
      
      let gameData;
      if (activeTemplate === 'matching') gameData = matchingPairs;
      else if (activeTemplate === 'sequencing') gameData = sequenceItems;
      else if (activeTemplate === 'sorting') gameData = sortGroups;
      else if (activeTemplate === 'mystery') gameData = mysteryData;

      const safeCreatorId = user?.uid || user?.id; 

      const finalActivityData = {
        title: activityTitle || draft.lessonTitle || "نشاط مدمج",
        coverImage: draft.coverImage || null,
        elements: draft.elements || [], 
        pageSettings: draft.pageSettings || { backgroundColor: '#ffffff', width: 800, height: 1000 },
        template: {
          type: activeTemplate,
          name: activeTemplate === 'matching' ? 'المطابقة' : activeTemplate === 'sequencing' ? 'الترتيب' : activeTemplate === 'sorting' ? 'التصنيف' : 'كشف الأسرار',
          data: gameData
        }, 
        type: 'lesson', 
        author: user?.name || "مؤطر تمكين",
        creatorId: safeCreatorId, 
        createdAt: serverTimestamp(),
      };

      if (!finalActivityData.creatorId) {
         throw new Error("لا يمكن الحفظ: الجلسة غير مصادق عليها أمنياً.");
      }

      const dataSize = JSON.stringify(finalActivityData).length;
      console.log("حجم النشاط التقريبي:", (dataSize / 1024 / 1024).toFixed(2), "MB");

      if (dataSize > 1048576) { 
        throw new Error("حجم الصور والفيديوهات كبير جداً! يرجى تقليل جودة الصور أو استخدام روابط بدلاً من الرفع المباشر.");
      }

      const docRef = await addDoc(collection(db, "library"), finalActivityData);
      console.log("تم الحفظ بنجاح، المعرف:", docRef.id);

      localStorage.removeItem('tamken_lesson_draft'); 
      
      alert("تم حفظ النشاط التفاعلي بنجاح! سيتم توجيهك الآن إلى قسم أنشطتك لتبدأ العرض.");
      navigate('/workshop', { state: { activeTab: 'my_activities' } });

    } catch (error) {
      console.error("فشل الحفظ:", error);
      alert("عذراً، لم يتم الحفظ: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const renderSidebar = () => (
    <aside className="w-[340px] bg-white border-l border-slate-200 flex flex-col shadow-2xl z-10 text-right shrink-0">
      <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
        <h2 className="font-black text-slate-800 text-[10px] uppercase tracking-widest">تخصيص القالب</h2>
        <button onClick={() => {
          if (activeTemplate === 'matching') {
            if (matchingPairs.length >= 15) { alert("الحد الأقصى 15 زوجاً (30 بطاقة)."); return; }
            setMatchingPairs([...matchingPairs, { id: Date.now().toString(), item1: {type:'text', content:''}, item2: {type:'text', content:''} }]);
          } else if (activeTemplate === 'sequencing') {
            if (sequenceItems.length >= 8) { alert("الحد الأقصى 8 عناصر."); return; }
            setSequenceItems([...sequenceItems, { id: Date.now().toString(), type: 'text', content: '' }]);
          } else if (activeTemplate === 'sorting') {
            if (sortGroups.length >= 5) { alert("الحد الأقصى 5 مواضيع للتصنيف."); return; }
            setSortGroups([...sortGroups, { id: Date.now().toString(), title: 'تصنيف جديد', items: [{ id: Date.now().toString(), type: 'text', content: '' }] }]);
          } else if (activeTemplate === 'mystery') {
            if (mysteryData.distractors.length >= 7) { alert("الحد الأقصى 7 كلمات خاطئة."); return; }
            setMysteryData({...mysteryData, distractors: [...mysteryData.distractors, '']});
          }
        }} className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md"><Plus size={16}/></button>      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
        {activeTemplate === 'matching' && (
          <>
            <div className="mb-4 flex items-center justify-between p-3 bg-blue-50 rounded-2xl border border-blue-100">
              <div className="flex items-center gap-2"><Copy size={14} className="text-blue-600"/><span className="text-[10px] font-bold text-blue-800">تكرار تلقائي</span></div>
              <button onClick={() => setIsIdenticalMode(!isIdenticalMode)} className={`w-10 h-5 rounded-full relative transition-all ${isIdenticalMode ? 'bg-blue-600' : 'bg-slate-300'}`}><div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${isIdenticalMode ? 'left-6' : 'left-1'}`}></div></button>
            </div>
            {matchingPairs.map(p => (
              <div key={p.id} className="p-3 bg-slate-50 border rounded-2xl relative group">
                <button onClick={() => setMatchingPairs(matchingPairs.filter(x => x.id !== p.id))} className="absolute -top-1 -left-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 z-10"><Trash2 size={10}/></button>
                <div className={`grid ${isIdenticalMode ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                  {['item1', 'item2'].map(k => (!isIdenticalMode || k === 'item1') && (
                    <div key={k} className="h-24 bg-white border rounded-xl relative flex items-center justify-center overflow-hidden">
                      {p[k].type === 'text' ? <textarea className="w-full h-full p-2 text-[10px] outline-none text-center font-bold" value={p[k].content} onChange={e => setMatchingPairs(matchingPairs.map(x => x.id === p.id ? {...x, [k]:{...p[k], content: e.target.value}} : x))} /> : <img src={p[k].content} className="w-full h-full object-contain p-1" />}
                      <div className="absolute bottom-1 right-1 flex gap-1 scale-75">
                        <button onClick={() => setMatchingPairs(matchingPairs.map(x => x.id === p.id ? {...x, [k]:{type:'text', content:''}} : x))} className={`p-1 rounded ${p[k].type==='text'?'bg-slate-800 text-white':'bg-slate-100'}`}><Type size={12}/></button>
                        <button onClick={() => triggerUpload(p.id, k)} className="p-1 rounded bg-slate-100"><ImageIcon size={12}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
        {activeTemplate === 'sequencing' && sequenceItems.map((item, index) => (
          <div key={item.id} className="p-3 bg-slate-50 border rounded-2xl relative flex gap-3 items-center">
             <div className="w-7 h-7 bg-slate-800 text-white rounded-full flex items-center justify-center font-black text-[10px]">{index + 1}</div>
             <div className="flex-1 h-20 bg-white border rounded-xl relative overflow-hidden flex items-center justify-center">
                {item.type === 'text' ? <textarea className="w-full h-full p-2 text-[10px] outline-none text-center font-bold" value={item.content} onChange={e => setSequenceItems(sequenceItems.map(s => s.id === item.id ? {...s, content: e.target.value} : s))} /> : <img src={item.content} className="w-full h-full object-contain p-1" />}
                <div className="absolute bottom-1 right-1 flex gap-1 scale-75">
                  <button onClick={() => setSequenceItems(sequenceItems.map(s => s.id === item.id ? {...s, type:'text', content:''}:s))} className={`p-1 rounded ${item.type==='text'?'bg-slate-800 text-white':'bg-slate-100'}`}><Type size={12}/></button>
                  <button onClick={() => triggerUpload(item.id, 'content')} className="p-1 rounded bg-slate-100"><ImageIcon size={12}/></button>
                </div>
             </div>
             <button onClick={() => setSequenceItems(sequenceItems.filter(s => s.id !== item.id))} className="text-red-400"><Trash2 size={16}/></button>
          </div>
        ))}
        {activeTemplate === 'sorting' && sortGroups.map(g => (
          <div key={g.id} className="p-4 bg-indigo-50/50 border rounded-2xl space-y-3">
             <div className="flex justify-between items-center"><input className="bg-transparent font-black text-indigo-900 outline-none text-xs w-24" value={g.title} onChange={e => setSortGroups(sortGroups.map(x => x.id === g.id ? {...x, title: e.target.value} : x))} /><button onClick={() => setSortGroups(sortGroups.filter(x => x.id !== g.id))} className="text-red-400"><Trash2 size={12}/></button></div>
             {g.items.map(it => (
                <div key={it.id} className="h-16 bg-white rounded-xl relative overflow-hidden flex items-center justify-center group/it shadow-sm">
                  {it.type === 'text' ? <textarea className="w-full h-full p-2 text-[9px] outline-none text-center" value={it.content} onChange={e => setSortGroups(sortGroups.map(x => x.id === g.id ? {...x, items: x.items.map(y => y.id === it.id ? {...y, content: e.target.value} : y)} : x))} /> : <img src={it.content} className="w-full h-full object-contain p-1" />}
                  <div className="absolute bottom-1 left-1 flex gap-1 scale-75">
                    <button onClick={() => setSortGroups(sortGroups.map(x => x.id === g.id ? {...x, items: x.items.map(y => y.id === it.id ? {...y, type:'text', content:''} : y)} : x))} className={`p-1 rounded ${it.type==='text'?'bg-indigo-600 text-white':'bg-slate-100'}`}><Type size={12}/></button>
                    {/* هنا يكمن الإصلاح: إرسال الـ subId لدعم رفع الصور في المجموعات */}
                    <button onClick={() => triggerUpload(g.id, 'sorting', it.id)} className="p-1 rounded bg-slate-100"><ImageIcon size={12}/></button>
                  </div>
                </div>
             ))}
             <button onClick={() => {
               if (g.items.length >= 5) { alert("الحد الأقصى 5 عناصر لكل موضوع."); return; }
               setSortGroups(sortGroups.map(x => x.id === g.id ? { ...x, items: [...x.items, { id: Date.now().toString(), type: 'text', content: '' }] } : x))
             }} className="w-full py-1.5 border border-dashed border-indigo-300 rounded-xl text-[9px] font-bold text-indigo-400">+ عنصر</button>          </div>
        ))}
        {activeTemplate === 'mystery' && (
          <div className="space-y-4">
             <div className="p-3 bg-slate-900 rounded-2xl border-2 border-slate-800"><label className="text-[10px] font-black text-white uppercase block text-center mb-2">كثافة الزجاج ({mysteryData.tileCount})</label><input type="range" min="16" max="48" step="4" value={mysteryData.tileCount} onChange={e => setMysteryData({...mysteryData, tileCount: parseInt(e.target.value)})} className="w-full accent-blue-500 h-1.5" /></div>
             <div className="h-40 bg-slate-100 rounded-2xl relative overflow-hidden flex items-center justify-center border-2 border-dashed border-slate-300">
               {mysteryData.image ? <img src={mysteryData.image} className="w-full h-full object-contain p-2" /> : <ImageIcon size={30} className="text-slate-300"/>}
               <button onClick={() => triggerUpload()} className="absolute inset-0 bg-black/40 text-white text-[10px] opacity-0 hover:opacity-100 transition-all font-bold">رفع صورة</button>
             </div>
             <div className="space-y-2">
                <input value={mysteryData.correctAnswer} onChange={e => setMysteryData({...mysteryData, correctAnswer: e.target.value})} className="w-full p-3 bg-slate-900 text-white rounded-xl text-xs font-bold border border-slate-800" placeholder="الإجابة الصحيحة" />
                {mysteryData.distractors.map((d, idx) => (
                  <div key={idx} className="flex gap-2"><input value={d} onChange={e => { const nd = [...mysteryData.distractors]; nd[idx] = e.target.value; setMysteryData({...mysteryData, distractors:nd}); }} className="flex-1 p-3 bg-slate-900 text-white rounded-xl text-xs border border-slate-800" placeholder="إجابة خاطئة" /><button onClick={() => setMysteryData({...mysteryData, distractors: mysteryData.distractors.filter((_, i) => i !== idx)})} className="text-red-400"><Trash2 size={16}/></button></div>
                ))}
             </div>
          </div>
        )}
        <div ref={scrollEndRef} />
      </div>
    </aside>
  );

const renderGame = () => {
    if (activeTemplate === 'matching') return <MatchingGame pairs={matchingPairs} onComplete={() => setMode('edit')} />;
    if (activeTemplate === 'sequencing') return <SequencingGame steps={sequenceItems} onComplete={() => setMode('edit')} />;
    if (activeTemplate === 'sorting') return <SortingGame categories={sortGroups} onComplete={() => setMode('edit')} />;
    if (activeTemplate === 'mystery') return <MysteryGame data={mysteryData} onComplete={() => setMode('edit')} />;
    return null;
  };
  
  return (
    <div className="h-screen w-screen flex flex-col bg-slate-950 overflow-hidden font-sans direction-rtl text-right text-slate-200">
      <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
      <header className="h-16 bg-[#1e293b] border-b border-slate-800 flex items-center justify-between px-8 z-30 shadow-2xl shrink-0">
        <div className="flex items-center gap-8">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-700 rounded-xl text-slate-400 transition-all"><ArrowRight size={24} /></button>
          <div className="relative" ref={menuRef}>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="flex items-center gap-4 px-6 py-2 bg-slate-800 border border-slate-700 rounded-[1.2rem] hover:bg-slate-700 transition-all shadow-inner group">
              <div className="p-1 bg-blue-600 rounded-lg text-white shadow-lg"><Layers size={14}/></div>
              <span className="text-[11px] font-black text-white uppercase tracking-widest">{activeTemplate === 'matching' ? 'المطابقة' : activeTemplate === 'sequencing' ? 'الترتيب' : activeTemplate === 'sorting' ? 'التصنيف' : 'كشف الأسرار'}</span>
              <ChevronDown size={14} className={`transition-transform duration-300 ${isMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {isMenuOpen && (
              <div className="absolute top-full right-0 mt-4 w-64 bg-[#1e293b] border border-slate-700 rounded-[1.8rem] shadow-2xl p-2 z-50 animate-in fade-in slide-in-from-top-4">
                {[{id:'matching', n:'المطابقة الثنائية'}, {id:'sequencing', n:'الترتيب الزمني'}, {id:'sorting', n:'التصنيف المجموعي'}, {id:'mystery', n:'كشف الأسرار'}].map(t => (
                  <button key={t.id} onClick={()=>{setActiveTemplate(t.id); setIsMenuOpen(false);}} className={`w-full flex items-center gap-4 p-3.5 rounded-[1.2rem] text-[11px] font-black mb-1 transition-all ${activeTemplate===t.id?'bg-blue-600 text-white shadow-lg':'hover:bg-slate-800 text-slate-400'}`}>{t.n}</button>
                ))}
              </div>
            )}
          </div>
          <input value={activityTitle} onChange={e=>setActivityTitle(e.target.value)} className="bg-transparent text-sm font-black text-white outline-none border-b-2 border-transparent focus:border-blue-600 transition-all px-2 w-64 italic" />
        </div>
        <div className="flex items-center gap-4">
          {mode === 'play' && <button onClick={() => setMode('edit')} className="px-5 py-2 bg-slate-700 text-white text-[10px] font-black rounded-xl transition-all hover:bg-slate-600 shadow-xl"><Settings size={16}/> العودة للتصميم</button>}
          <button onClick={initializeGame} className="px-10 py-2 bg-blue-600 text-white text-[11px] font-black rounded-xl shadow-lg hover:bg-blue-500 active:scale-95 transition-all">تشغيل العرض</button>
          <button onClick={handleFinalSave} disabled={isSaving} className="px-6 py-2 bg-emerald-600 text-white text-[11px] font-black rounded-xl shadow-xl hover:bg-emerald-500 transition-all disabled:opacity-50 flex items-center gap-2">
            <Save size={16} /> {isSaving ? 'جاري الحفظ...' : 'حفظ ونشر'}
          </button>        
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden bg-slate-100">
        {mode === 'edit' ? (
          <>
            {renderSidebar()}
            <div className="flex-1 flex items-center justify-center p-12 relative overflow-auto custom-scrollbar bg-slate-200">
               <div className="text-center p-16 bg-white rounded-[5rem] shadow-2xl w-full max-w-2xl border border-white mx-10 relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                  <div className="w-32 h-32 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-10 shadow-inner relative z-10 transition-transform group-hover:scale-110 duration-700">
                    {activeTemplate==='matching'?<Layers size={60}/>:activeTemplate==='sorting'?<Grid size={60}/>:activeTemplate==='sequencing'?<Film size={60}/>:<Eye size={60}/>}
                  </div>
                  <h2 className="text-4xl font-black text-slate-900 mb-6 tracking-tighter uppercase relative z-10 italic">استوديو تمكين</h2>
                  <p className="text-slate-400 text-lg mb-10 font-bold relative z-10">أنت تقوم الآن بتعديل قالب <b>{activeTemplate}</b> بدقة، ثم اضغط زر التشغيل لاختبار النتيجة.</p>
                  <button onClick={initializeGame} className="w-full py-6 bg-slate-950 text-white font-black rounded-[2.5rem] hover:bg-black transition-all transform hover:scale-[1.03] shadow-[0_20px_40px_rgba(0,0,0,0.4)] tracking-[0.4em] uppercase relative z-10 text-lg">تـشــغـيـل العــرض</button>
               </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {renderGame()}
          </div>
        )}      
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 20px; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-20px); } 40% { transform: translateX(20px); } 60% { transform: translateX(-20px); } 80% { transform: translateX(20px); } }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}} />
    </div>
  );
};

export default ActivityStudio;